import { useCallback } from 'react'
import { NavigateOptions, To, useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

type FundingNavigateOptions = Omit<NavigateOptions, 'state'> & {
  state?: unknown
  preserveState?: boolean
}

type FundingEntryNavigateOptions = Omit<NavigateOptions, 'state'> & {
  state?: Record<string, unknown>
}

export type FundingLocationState = {
  returnPath?: string
  isDestinationPreselected?: boolean
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const proto = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

const mergePreservingUndefined = (
  preserved: Record<string, unknown>,
  extra: Record<string, unknown>
): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...preserved }
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined) out[k] = v
  }
  return out
}

export const useFundingNav = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const returnPath = `${location.pathname}${location.search}`

  const navigateToFunding = useCallback(
    (to: To, opts?: FundingEntryNavigateOptions) => {
      const { state, ...navOpts } = opts ?? {}
      navigate(to, { ...navOpts, state: { ...state, returnPath } })
    },
    [navigate, returnPath]
  )

  const go = useCallback(
    (to: To | number, opts?: FundingNavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to)
        return
      }

      const preserve = opts?.preserveState !== false
      const extraState = opts?.state
      const preservedState = preserve ? location.state : undefined

      let mergedState: unknown

      if (isPlainObject(preservedState) && isPlainObject(extraState)) {
        mergedState = mergePreservingUndefined(preservedState, extraState)
      } else if (extraState !== undefined) {
        mergedState = extraState
      } else {
        mergedState = preservedState
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { preserveState: _preserveState, ...navOpts } = opts ?? {}
      navigate(to, { ...navOpts, state: mergedState })
    },
    [location.state, navigate]
  )

  return { go, navigateToFunding, returnPath, state: location.state }
}
